import {
  Box,
  Combobox,
  InputBase,
  Stack,
  TextInput,
  useCombobox,
  type ComboboxItem,
  type ComboboxItemGroup,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setSelectedBoards } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import { baseBlack, baseWhite, inkLight, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Checkbox } from '../../../../ui-library/checkbox/checkbox';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import classes from '../../../../ui-library/select/select.module.css';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { formatBoardsForCombobox, getDefaultSelectedBoard } from './main-section.helpers';
import { usePlaceholderText } from './main-section.hooks';

const MAX_COMPARISON_BOARDS = 4;

export const BoardComparisonSelector = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const globalSelectedBoardIds = useProcessAnalysisStore((state) => state.selectedBoards);
  const [localSelectedBoardIds, setLocalSelectedBoardIds] = useState<string[]>([]);
  const teams = useGlobalStore((state) => state.teams);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchTerm('');
    },
  });

  // Initialize with first board if no selection
  useEffect(() => {
    if (teams.length > 0 && globalSelectedBoardIds.length === 0) {
      const defaultBoard = getDefaultSelectedBoard(teams);

      if (defaultBoard) {
        setSelectedBoards([defaultBoard]);
      }
    }
  }, [teams, globalSelectedBoardIds.length]);

  useEffect(() => {
    setLocalSelectedBoardIds(globalSelectedBoardIds);
  }, [globalSelectedBoardIds]);

  const placeholderText = usePlaceholderText();
  const boardGroups = formatBoardsForCombobox(teams, searchTerm);

  const { t } = useTranslation();

  const handleBoardToggle = (boardId: string) => {
    setLocalSelectedBoardIds((current) => {
      if (current.includes(boardId)) {
        return current.filter((id) => id !== boardId);
      } else if (current.length < MAX_COMPARISON_BOARDS) {
        return [...current, boardId];
      }
      return current;
    });
  };

  const handleCompare = () => {
    setSelectedBoards(localSelectedBoardIds);
    combobox.closeDropdown();
  };

  const isCompareDisabled = localSelectedBoardIds.length === 0;

  const options = boardGroups.map((group: ComboboxItemGroup<ComboboxItem, string>) => (
    <Box key={group.group} mb={16}>
      <Text size="small" weight="bold">
        {group.group}
      </Text>

      {group.items.length > 0 && (
        <Stack gap={16} ml={16} mt={16}>
          {(group.items as ComboboxItem[]).map((item: ComboboxItem) => {
            const isChecked = localSelectedBoardIds.includes(item.value);
            const disabled = !isChecked && localSelectedBoardIds.length >= MAX_COMPARISON_BOARDS;

            return (
              <Tooltip
                key={item.value}
                label={t('common.tooltips.max_comparison_limit', {
                  count: MAX_COMPARISON_BOARDS,
                })}
                variant="white"
                disabled={!disabled}
                position="right"
              >
                <Combobox.Option value={item.value} key={item.value} style={{ padding: 0 }}>
                  <Checkbox
                    label={item.label}
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => handleBoardToggle(item.value)}
                    size="sm"
                  />
                </Combobox.Option>
              </Tooltip>
            );
          })}
        </Stack>
      )}
    </Box>
  ));

  return (
    <Combobox store={combobox} position="bottom-start" offset={{ mainAxis: 4 }}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          radius="xl"
          onClick={() => combobox.toggleDropdown()}
          leftSection={<Icon name="view_kanban" size={16} color={skyDark} />}
          leftSectionPointerEvents="none"
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          pointer
          classNames={classes}
          styles={{
            input: {
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '12px',
              color: localSelectedBoardIds.length > 0 ? baseBlack : skyDark,
              cursor: 'pointer',
              textAlign: 'left',
              width: 300,
              height: 30,
              minHeight: 30,
              fontFamily: 'Figtree',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            },
          }}
        >
          {placeholderText}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown p={16} style={{ borderRadius: 8 }}>
        <Stack gap={16}>
          <TextInput
            placeholder={t('process_analysis.main_section.search_by_project_or_board_name')}
            radius="xl"
            size="sm"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
            style={{ width: '100%' }}
            classNames={classes}
          />
          <Divider />
          {options.length > 0 ? (
            <Combobox.Options
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                padding: 0,
              }}
            >
              {options}
            </Combobox.Options>
          ) : (
            <Box style={{ textAlign: 'center' }}>
              <Text size="small" color={skyDark}>
                {searchTerm
                  ? t('process_analysis.main_section.no_boards_found')
                  : t('process_analysis.main_section.no_boards_available')}
              </Text>
            </Box>
          )}
          <Divider />
          <Button
            variant="secondary"
            radius="xl"
            size="xs"
            leftSection={
              <Icon
                name={localSelectedBoardIds.length > 1 ? 'compare_arrows' : 'check'}
                color={isCompareDisabled ? secondaryBase : baseWhite}
                size={14}
                pointer
              />
            }
            fullWidth
            onClick={handleCompare}
            disabled={isCompareDisabled}
          >
            {localSelectedBoardIds.length > 1 ? t('common.actions.compare') : t('common.actions.apply')}
          </Button>
        </Stack>
      </Combobox.Dropdown>
    </Combobox>
  );
};
